import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { IPermissionCode, usePermission } from "../permission"
import { IAuth } from "../../store/Auth"

/**
 * Supplier sales: the ONE place that says which action exists in which order status and which
 * permission it needs (canvas Ord_SalesModals 2.4.6 + Ord_Actions). Permission codes match the
 * backend routes in de-order-service/src/routes/api/order/*.ts; several codes mean "any of".
 */
export type SalesAction =
  | "detail"
  | "edit"
  | "review"
  | "approve"
  | "decline"
  | "cancel"
  | "cancelConfirmed"
  | "deliveryCreate"
  | "backorder"
  | "split"

export const SALES_ACTION_PERMISSION: Record<SalesAction, IPermissionCode[]> = {
  detail: ["ORD_SO_DETAIL"],
  edit: ["ORD_SAVE", "ORD_SEND"], // BE order/update.ts
  review: ["ORD_EDIT", "ORD_SEND"], // BE order/review.ts
  approve: ["ORD_PO_ACCEPT"], // BE order/respond.ts, approve=true
  decline: ["ORD_PO_DECLINE"], // BE order/respond.ts, approve=false
  cancel: ["ORD_CANCEL"], // BE order/cancel.ts
  cancelConfirmed: ["ORD_CANCEL"], // BE order/cancel-confirmed.ts
  deliveryCreate: ["ORD_DN_CREATE"], // BE delivery-note/create.ts
  backorder: ["ORD_BCK_ORD"], // BE order/backorder/create.ts
  split: ["ORD_SPLIT"], // BE order/split/create.ts
}

/** Statuses the backend lets the sender change (order/update.ts). */
export const EDITABLE_STATUSES = ["DRAFT", "REGISTERED", "REVIEW_REJECTED"] as const

export type SalesOrderLike = {
  type?: string | null
  orderStatus?: string | null
  senderBusiness?: { id?: string | null } | null
  receiverBusiness?: { id?: string | null } | null
  deliveryNote?: unknown
  salesType?: string | null
}

type Side = { isSender: boolean; isReceiver: boolean }

/** Status x action matrix. Role (sender/receiver) is part of the rule because the backend scopes by it. */
const APPLIES: Record<SalesAction, (o: SalesOrderLike, side: Side) => boolean> = {
  detail: () => true,
  edit: (o) => EDITABLE_STATUSES.includes(o.orderStatus as (typeof EDITABLE_STATUSES)[number]),
  review: (o, s) => o.orderStatus === "REGISTERED" && s.isSender,
  approve: (o, s) => o.orderStatus === "REVIEWED" && s.isReceiver,
  decline: (o, s) => o.orderStatus === "REVIEWED" && s.isReceiver,
  cancel: (o, s) => o.orderStatus === "REVIEWED" && s.isSender,
  cancelConfirmed: (o, s) => o.orderStatus === "AUTHORIZED" && s.isSender,
  deliveryCreate: (o, s) => o.orderStatus === "AUTHORIZED" && !o.deliveryNote && s.isSender,
  // BE backorder/get-lines.ts + create.ts: a PURCHASE order in REVIEWED addressed to this business (supplier purchase list).
  backorder: (o, s) => o.type === "PURCHASE" && o.orderStatus === "REVIEWED" && s.isReceiver,
  split: (o) => o.salesType !== "SPLIT" && ["AUTHORIZED", "REGISTERED", "REVIEWED"].includes(o.orderStatus ?? ""),
}

export type SalesActionState = {
  /** The action belongs to this status at all; when false, render nothing. */
  applies: boolean
  /** The user may perform it. */
  allowed: boolean
  /** Why it is unavailable (permission), null when allowed. */
  reason: string | null
}

/** Pure status check used by tests and the permission hook below. */
export function salesActionApplies(action: SalesAction, order: SalesOrderLike | null | undefined, side: Side): boolean {
  if (!order) return false
  return APPLIES[action](order, side)
}

/**
 * Returns `stateOf(action, order)`. Every call site (list row, detail header, bulk toolbar) uses it,
 * so a status or permission change is made once. An action that applies but is not permitted stays
 * visible and carries the reason ("Эрх хэрэгтэй: КОД"), it is never hidden.
 */
export function useSalesActions() {
  const { t } = useTranslation("common")
  const user = useSelector((s: { auth: IAuth }) => s.auth.user)
  const myBusinessId = user?.currentBusiness?.id

  const granted: Partial<Record<IPermissionCode, boolean>> = {
    ORD_SO_DETAIL: usePermission("ORD_SO_DETAIL").hasAccess,
    ORD_SAVE: usePermission("ORD_SAVE").hasAccess,
    ORD_SEND: usePermission("ORD_SEND").hasAccess,
    ORD_EDIT: usePermission("ORD_EDIT").hasAccess,
    ORD_PO_ACCEPT: usePermission("ORD_PO_ACCEPT").hasAccess,
    ORD_PO_DECLINE: usePermission("ORD_PO_DECLINE").hasAccess,
    ORD_CANCEL: usePermission("ORD_CANCEL").hasAccess,
    ORD_DN_CREATE: usePermission("ORD_DN_CREATE").hasAccess,
    ORD_BCK_ORD: usePermission("ORD_BCK_ORD").hasAccess,
    ORD_SPLIT: usePermission("ORD_SPLIT").hasAccess,
  }

  const permissionState = useCallback(
    (action: SalesAction) => {
      const codes = SALES_ACTION_PERMISSION[action]
      const allowed = codes.some((c) => granted[c])
      return { allowed, reason: allowed ? null : t("permission.required", { code: codes.join(" / ") }) }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, JSON.stringify(granted)],
  )

  const stateOf = useCallback(
    (action: SalesAction, order: SalesOrderLike | null | undefined): SalesActionState => {
      const side: Side = {
        isSender: !!myBusinessId && myBusinessId === order?.senderBusiness?.id,
        isReceiver: !!myBusinessId && myBusinessId === order?.receiverBusiness?.id,
      }
      const applies = salesActionApplies(action, order, side)
      return { applies, ...permissionState(action) }
    },
    [myBusinessId, permissionState],
  )

  return { stateOf, permissionState }
}
